import { useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Papa from "papaparse";
import { X, Upload, Check, AlertCircle, FileText, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  CATEGORIES,
  EXTERIOR_SERIES,
  INTERIOR_SERIES,
  GARAGE_SERIES,
  FINISHES,
  HANDLE_COLLECTIONS,
  HARDWARE_FINISHES,
  GLASS_OPTIONS,
} from "@/lib/catalog";
import { useQuote } from "@/context/QuoteContext";

const HEADER_ALIASES = {
  category: "category",
  type: "category",
  door_type: "category",
  series: "series",
  model: "series",
  style: "series",
  finish: "finish",
  material: "finish",
  color: "color",
  colour: "color",
  panel: "panel_style",
  panel_style: "panel_style",
  hardware: "hardware_collection",
  hardware_collection: "hardware_collection",
  handle: "hardware_collection",
  hardware_finish: "hardware_finish",
  glass: "glass",
  width: "width",
  w: "width",
  height: "height",
  h: "height",
  size: "size",
  quantity: "quantity",
  qty: "quantity",
  count: "quantity",
  notes: "notes",
  note: "notes",
};

const CSV_TEMPLATE = `category,series,finish,color,width,height,quantity,glass,hardware_collection,notes
Interior,Forma,Melamine,Nordic Maple,32",96",6,None,Barcelona,Bedrooms L1
Interior,Siena,High-Quality Paint,Pure White,28",84",4,None,Milan,Closets
Exterior,Toscana,Wood Veneer,Natural Oak,48,96,1,None,,Front entry — pivot
Garage,Denver,,Warm Walnut,192,84,1,,,Main garage`;

const norm = (v) => (v == null ? "" : String(v).trim());
const normLower = (v) => norm(v).toLowerCase();

function validCategory(v) {
  const n = normLower(v);
  return CATEGORIES.find((c) => c.toLowerCase() === n) || null;
}

function seriesFor(category) {
  if (category === "Exterior") return EXTERIOR_SERIES.map((s) => s.name);
  if (category === "Interior") return INTERIOR_SERIES.map((s) => s.name);
  if (category === "Garage") return GARAGE_SERIES.map((s) => s.name);
  return [];
}

function matchIgnoreCase(list, value) {
  if (!value) return null;
  const n = normLower(value);
  return list.find((x) => x.toLowerCase() === n) || null;
}

function parseSize(v) {
  const n = norm(v);
  if (!n) return "";
  return n.endsWith('"') ? n : n;
}

export default function CsvImportModal({ open, onClose }) {
  const { addToCart } = useQuote();
  const [text, setText] = useState("");
  const [rows, setRows] = useState([]); // [{ index, item, warnings, errors, valid }]
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.valid).length;
    const errors = rows.filter((r) => !r.valid).length;
    const totalDoors = rows.filter((r) => r.valid).reduce((s, r) => s + (r.item.quantity || 1), 0);
    return { total: rows.length, valid, errors, totalDoors };
  }, [rows]);

  const reset = () => {
    setText("");
    setRows([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const parse = (csvText) => {
    const parsed = Papa.parse(csvText.trim(), {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => {
        const key = h.trim().toLowerCase().replace(/\s+/g, "_");
        return HEADER_ALIASES[key] || key;
      },
    });
    if (parsed.errors?.length) {
      toast.error(`CSV parse issues: ${parsed.errors[0].message}`);
    }
    const out = (parsed.data || []).map((raw, i) => validateRow(raw, i));
    setRows(out);
    if (out.length === 0) {
      toast.error("No rows found. Add at least one row after the header.");
    } else {
      toast.success(`Parsed ${out.length} row(s).`);
    }
  };

  const validateRow = (raw, index) => {
    const warnings = [];
    const errors = [];

    const category = validCategory(raw.category);
    if (!category) errors.push(`Row ${index + 2}: category must be Exterior, Interior or Garage`);

    let series = null;
    if (raw.series && category) {
      series = matchIgnoreCase(seriesFor(category), raw.series);
      if (!series) warnings.push(`series "${raw.series}" not recognised for ${category} — kept as text`);
      series = series || norm(raw.series);
    }

    let finish = null;
    if (raw.finish) {
      const finishNames = Object.keys(FINISHES);
      finish = matchIgnoreCase(finishNames, raw.finish) || norm(raw.finish);
      if (!Object.keys(FINISHES).some((f) => f.toLowerCase() === normLower(raw.finish))) {
        warnings.push(`finish "${raw.finish}" not standard — kept as text`);
      }
    }

    let color = norm(raw.color) || null;

    let panel_style = norm(raw.panel_style) || null;

    let hardware_collection = null;
    if (raw.hardware_collection) {
      hardware_collection =
        matchIgnoreCase(HANDLE_COLLECTIONS.map((h) => h.name), raw.hardware_collection) ||
        norm(raw.hardware_collection);
    }

    let hardware_finish = null;
    if (raw.hardware_finish) {
      hardware_finish =
        matchIgnoreCase(HARDWARE_FINISHES.map((f) => f.name), raw.hardware_finish) || norm(raw.hardware_finish);
    }

    let glass = null;
    if (raw.glass) {
      glass = matchIgnoreCase(GLASS_OPTIONS, raw.glass) || norm(raw.glass);
    }

    // Size — split "size" column like 32x96 if provided
    let width = parseSize(raw.width);
    let height = parseSize(raw.height);
    if ((!width || !height) && raw.size) {
      const m = norm(raw.size).match(/^\s*(\d+["]?)\s*[xX×]\s*(\d+["]?)\s*$/);
      if (m) {
        width = parseSize(m[1]);
        height = parseSize(m[2]);
      }
    }

    let quantity = parseInt(raw.quantity, 10);
    if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;

    const item = {
      category,
      series,
      finish,
      color,
      panel_style,
      hardware_collection,
      hardware_finish,
      glass,
      width,
      height,
      quantity,
      notes: norm(raw.notes) || null,
    };
    return {
      index,
      item,
      warnings,
      errors,
      valid: errors.length === 0,
    };
  };

  const onFile = (fileList) => {
    const f = Array.from(fileList || [])[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = String(e.target.result || "");
      setText(content);
      parse(content);
    };
    reader.readAsText(f);
  };

  const importValid = () => {
    const valid = rows.filter((r) => r.valid);
    if (!valid.length) {
      toast.error("No valid rows to import.");
      return;
    }
    valid.forEach((r) => addToCart(r.item));
    toast.success(`Imported ${valid.length} item(s) into your order · ${summary.totalDoors} doors`);
    reset();
    onClose();
  };

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(CSV_TEMPLATE);
      toast.success("Template copied to clipboard.");
    } catch {
      toast.error("Could not copy — please select and copy manually.");
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "atelier-door-schedule-template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[95] bg-brand-charcoal/70 backdrop-blur"
            onClick={handleClose}
            data-testid="csv-modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[100] flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-5xl bg-brand-bone border border-brand-green/25 shadow-2xl"
              data-testid="csv-modal"
            >
              <div className="relative bg-brand-green text-brand-bone p-6 md:p-8">
                <button
                  className="absolute top-4 right-4 text-brand-bone/70 hover:text-brand-gold"
                  onClick={handleClose}
                  data-testid="csv-modal-close"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
                <span className="text-[0.7rem] tracking-[0.32em] uppercase text-brand-gold">
                  Bulk Import
                </span>
                <h3 className="mt-3 font-serif text-3xl md:text-4xl">
                  Paste your <span className="italic text-brand-gold">door schedule.</span>
                </h3>
                <p className="mt-2 text-brand-bone/75 text-sm max-w-xl">
                  Turn any spreadsheet into a configured order. Copy your columns, drop a CSV, or use
                  our template — every row becomes an item in the cart.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 md:p-8">
                <div className="md:col-span-7 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60">
                      CSV Content
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={copyTemplate}
                        className="inline-flex items-center gap-1 text-[0.62rem] tracking-[0.24em] uppercase text-brand-green hover:text-brand-gold"
                        data-testid="csv-copy-template"
                      >
                        <Copy size={12} /> Copy Template
                      </button>
                      <button
                        type="button"
                        onClick={downloadTemplate}
                        className="inline-flex items-center gap-1 text-[0.62rem] tracking-[0.24em] uppercase text-brand-green hover:text-brand-gold"
                        data-testid="csv-download-template"
                      >
                        <FileText size={12} /> Download .csv
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="min-h-[240px] w-full bg-white border border-brand-ink/20 p-3 text-sm font-mono text-brand-ink focus:outline-none focus:border-brand-green"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={CSV_TEMPLATE}
                    spellCheck={false}
                    data-testid="csv-textarea"
                  />
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(e) => { e.preventDefault(); setDragOver(false); onFile(e.dataTransfer.files); }}
                      onClick={() => fileInputRef.current?.click()}
                      className={`flex-1 min-w-[240px] cursor-pointer border-2 border-dashed p-3 flex items-center gap-3 text-sm transition-colors ${
                        dragOver ? "border-brand-gold bg-brand-gold/10" : "border-brand-green/25 hover:border-brand-green"
                      }`}
                      data-testid="csv-dropzone"
                    >
                      <Upload size={16} className="text-brand-green" />
                      <span className="text-brand-ink">
                        <span className="font-medium">Drop a .csv file</span> or click to browse
                      </span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,text/csv,text/plain"
                        hidden
                        onChange={(e) => { onFile(e.target.files); e.target.value = ""; }}
                        data-testid="csv-file-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => parse(text)}
                      disabled={!text.trim()}
                      className={`btn-primary ${!text.trim() ? "opacity-40 pointer-events-none" : ""}`}
                      data-testid="csv-parse-button"
                    >
                      Parse Rows →
                    </button>
                  </div>
                </div>

                <div className="md:col-span-5 flex flex-col gap-3 border border-brand-green/15 bg-white/70 p-4">
                  <div className="flex items-baseline justify-between">
                    <div>
                      <div className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60">
                        Preview
                      </div>
                      <div className="mt-1 font-serif text-brand-green text-2xl">
                        {summary.total > 0
                          ? `${summary.valid}/${summary.total} valid`
                          : "—"}
                      </div>
                    </div>
                    {summary.valid > 0 && (
                      <div className="text-right">
                        <div className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-ink/50">
                          Doors
                        </div>
                        <div className="font-serif italic text-brand-gold text-3xl leading-none tabular-nums">
                          {summary.totalDoors}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="max-h-[280px] overflow-y-auto -mx-1 pr-1" data-testid="csv-preview-list">
                    {rows.length === 0 && (
                      <div className="text-sm text-brand-ink/50 py-6 text-center border border-dashed border-brand-green/20 mt-2">
                        Parsed rows will appear here.
                      </div>
                    )}
                    {rows.map((r) => (
                      <div
                        key={r.index}
                        className={`mt-2 border p-3 ${
                          r.valid ? "border-brand-green/20 bg-brand-bone-2" : "border-red-400/40 bg-red-50/60"
                        }`}
                        data-testid={`csv-row-${r.index}`}
                      >
                        <div className="flex items-baseline justify-between">
                          <div className="flex items-center gap-2">
                            {r.valid ? (
                              <Check size={14} className="text-green-700" />
                            ) : (
                              <AlertCircle size={14} className="text-red-700" />
                            )}
                            <span className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-ink/60">
                              Row {r.index + 2}
                            </span>
                            <span className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-gold">
                              {r.item.category || "—"}
                            </span>
                            {r.item.series && (
                              <span className="font-serif italic text-brand-green">{r.item.series}</span>
                            )}
                          </div>
                          <span className="font-serif italic text-brand-green">×{r.item.quantity}</span>
                        </div>
                        <div className="text-xs text-brand-ink/70 mt-1 truncate">
                          {[r.item.finish, r.item.color, r.item.width && r.item.height ? `${r.item.width}×${r.item.height}` : null]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </div>
                        {r.warnings.length > 0 && (
                          <div className="text-[0.62rem] tracking-[0.14em] uppercase text-amber-700 mt-1">
                            {r.warnings.join(" · ")}
                          </div>
                        )}
                        {r.errors.length > 0 && (
                          <div className="text-[0.62rem] tracking-[0.14em] uppercase text-red-700 mt-1">
                            {r.errors.join(" · ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-3 mt-1 border-t border-brand-green/15">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="text-[0.72rem] tracking-[0.28em] uppercase text-brand-ink/60 hover:text-brand-green"
                      data-testid="csv-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={importValid}
                      disabled={summary.valid === 0}
                      className={`btn-primary ${summary.valid === 0 ? "opacity-40 pointer-events-none" : ""}`}
                      data-testid="csv-import-button"
                    >
                      Import {summary.valid} item(s) →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
